/* eslint-disable max-len */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { ReactSelectOption } from 'src/types/ReactSelectOption';
import { DataEntryMode } from '../../types/DataEntryMode';
import { SeniorSnapshotFileListItemModel } from '../../types/SeniorSnapshotFileListItemModel';
import { SeniorSnapshotFormViewModel } from '../../types/SeniorSnapshotFormViewModel';
import { SeniorSnapshotListItemModel } from '../../types/SeniorSnapshotListItemModel';
import { SeniorSnapshotModel } from '../../types/SeniorSnapshotModel';
import { isEmptyObject } from '../../utilities/helperUtilities';
import type { RootState } from '../store';
import {
  deleteSeniorSnapshotFileThunk,
  fetchAllSeniorSnapshotFilesThunk,
  fetchDataCollectionCheck,
  fetchSeniorSnapshotById,
  fetchSeniorSnapshotFilesByStudentThunk,
  fetchSeniorSnapshotLeas,
  fetchSeniorSnapshotListItems,
  fetchSeniorSnapshotSchoolsByLeaId,
  fetchSeniorSnapshotSchoolYears,
  uploadSeniorSnapshotFileThunk,
} from '../thunks/seniorSnapshotThunks';

interface SeniorSnapshotState {
  schoolYear: string;
  school: string;
  loadingListItems: boolean;
  listItems: SeniorSnapshotListItemModel[];

  loadingSeniorSnapshot: boolean;
  seniorSnapshot: SeniorSnapshotModel;

  loadingFormViewModel: boolean;
  formViewModel: SeniorSnapshotFormViewModel;

  dataEntryMode: DataEntryMode;

  uploadingFile: boolean;
  downloadingFile: boolean;
  deletingFile: boolean;
  files: SeniorSnapshotFileListItemModel[];
  studentFiles: SeniorSnapshotFileListItemModel[];
  loadingFiles: boolean;
  loadingStudentFiles: boolean;
  error: string | null;
  dataCollectionCheck: boolean;
}

export const defaultListState: SeniorSnapshotListItemModel[] = [];

export const defaultSeniorSnapshotState: SeniorSnapshotModel = {
  id: '',
  schoolYear: '',
  studentIdentifierState: '',
  studentLastOrSurname: '',
  studentFirstName: '',
  studentMiddleName: '',
  birthDate: '',
  scdIndicator: '',
  districtName: '',
  leaIdentifier: '',
  schoolName: '',
  schoolId: '',
  gender: '',
  ethnicity: '',
  race: '',
  algebraParticipated: '',
  algebraReason: '',
  algebraTransferDate: '',
  algebraTransferredFromSchoolName: '',
  algebraAccreditedSchool: '',
  englishParticipated: '',
  englishReason: '',
  englishTransferDate: '',
  englishTransferredFromSchoolName: '',
  englishAccreditedSchool: '',
  biologyParticipated: '',
  biologyReason: '',
  biologyTransferDate: '',
  biologyTransferredFromSchoolName: '',
  biologyAccreditedSchool: '',
  historyParticipated: '',
  historyReason: '',
  historyTransferDate: '',
  historyNameofSchoolStudentTransferredfrom: '',
  historyAccreditedSchool: '',
  documentsUploadedToSharePoint: false,
  reviewStatus: '',
  denialReason: '',
  studentUpdate: false,
};

export const defaultFormState: SeniorSnapshotFormViewModel = {
  defaultSchoolYear: { value: '', label: '' },
  selectedSchoolYear: { value: '', label: '' },
  schoolYearOptions: [],
  selectedLea: { value: '', label: '' },
  leaOptions: [],
  selectedSchool: { value: '', label: '' },
  schoolOptions: [],
  reasonOptions: [
    {
      text: 'Transferred from Out of State',
      value: 'Transferred from Out of State',
    },
    {
      text: 'Transferred from Private School',
      value: 'Transferred from Private School',
    },
  ],
  accreditedSchoolOptions: [
    {
      text: 'Yes',
      value: 'Yes',
    },
    {
      text: 'No',
      value: 'No',
    },
  ],
  selectedReviewStatus: { value: '', label: '' },
  reviewStatusOptions: [
    { text: '\xa0', value: '', isDisabled: true },
    {
      text: 'Pending Approval',
      value: 'Pending Approval',
      isDisabled: true,
    },
    {
      text: 'Approved',
      value: 'Approved',
      isDisabled: false,
    },
    {
      text: 'Denied',
      value: 'Denied',
      isDisabled: false,
    },
  ],
};

export const dataEntryMode = DataEntryMode.NEW;

export const initialState: SeniorSnapshotState = {
  schoolYear: '',
  school: '',
  loadingListItems: false,
  listItems: [],
  loadingSeniorSnapshot: false,
  seniorSnapshot: defaultSeniorSnapshotState,
  loadingFormViewModel: false,
  formViewModel: defaultFormState,
  dataEntryMode: dataEntryMode,
  uploadingFile: false,
  downloadingFile: false,
  deletingFile: false,
  files: [],
  studentFiles: [],
  loadingFiles: false,
  loadingStudentFiles: false,
  error: null,
  dataCollectionCheck: false,
};

export const seniorSnapshotSlice = createSlice({
  name: 'seniorSnapshot',
  initialState,
  reducers: {
    clearSeniorSnapshot: (state) => {
      state.seniorSnapshot = defaultSeniorSnapshotState;
    },
    saveSeniorSnapshot: (state, action: PayloadAction<SeniorSnapshotModel>) => {
      state.seniorSnapshot = action.payload;
    },
    setSeniorSnapshotDataEntryMode: (
      state,
      action: PayloadAction<DataEntryMode>
    ) => {
      state.dataEntryMode = action.payload;
    },
    setSeniorSnapshotSchoolYear: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchoolYear = action.payload;
    },
    setSeniorSnapshotLea: (state, action: PayloadAction<ReactSelectOption>) => {
      state.formViewModel.selectedLea = action.payload;
    },
    setSeniorSnapshotSchool: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedSchool = action.payload;
    },
    setSeniorSnapshotReviewStatus: (
      state,
      action: PayloadAction<ReactSelectOption>
    ) => {
      state.formViewModel.selectedReviewStatus = action.payload;
    },
    setUploadingFile: (state, action: PayloadAction<boolean>) => {
      state.uploadingFile = action.payload;
    },
    setDownloadingFile: (state, action: PayloadAction<boolean>) => {
      state.downloadingFile = action.payload;
    },
    setDeletingFile: (state, action: PayloadAction<boolean>) => {
      state.deletingFile = action.payload;
    },
    setFiles: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel[]>
    ) => {
      state.files = action.payload;
    },
    addFile: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel>
    ) => {
      state.files.push(action.payload);
    },
    updateFile: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel>
    ) => {
      const index = state.files.findIndex(
        (file) => file.id === action.payload.id
      );
      if (index !== -1) {
        state.files[index] = action.payload;
      }
    },
    removeFile: (state, action: PayloadAction<string>) => {
      state.files = state.files.filter((file) => file.id !== action.payload);
    },
    setStudentFiles: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel[]>
    ) => {
      state.studentFiles = action.payload;
    },
    addStudentFile: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel>
    ) => {
      state.studentFiles.push(action.payload);
    },
    updateStudentFile: (
      state,
      action: PayloadAction<SeniorSnapshotFileListItemModel>
    ) => {
      const index = state.studentFiles.findIndex(
        (file) => file.id === action.payload.id
      );
      if (index !== -1) {
        state.studentFiles[index] = action.payload;
      }
    },
    removeStudentFile: (state, action: PayloadAction<string>) => {
      state.studentFiles = state.studentFiles.filter(
        (file) => file.id !== action.payload
      );
    },
    clearSeniorSnapshotError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchLeaManagementList
    builder.addCase(fetchSeniorSnapshotListItems.pending, (state) => {
      state.listItems = defaultListState;
      state.loadingListItems = true;
    });
    builder.addCase(fetchSeniorSnapshotListItems.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.listItems = action.payload;
      }
      state.loadingListItems = false;
    });
    builder.addCase(fetchSeniorSnapshotListItems.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingListItems = false;
      }
    });

    // fetchSeniorSnapshotById
    builder.addCase(fetchSeniorSnapshotById.pending, (state) => {
      state.seniorSnapshot = defaultSeniorSnapshotState;
      state.loadingSeniorSnapshot = true;
    });
    builder.addCase(fetchSeniorSnapshotById.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        const seniorSnapshot = { ...action.payload };
        if (
          !seniorSnapshot.reviewStatus ||
          seniorSnapshot.reviewStatus === ''
        ) {
          seniorSnapshot.reviewStatus = '';
        }

        if (seniorSnapshot.districtName && seniorSnapshot.leaIdentifier) {
          seniorSnapshot.districtName = `${seniorSnapshot.districtName} (${seniorSnapshot.leaIdentifier})`;
        }

        if (seniorSnapshot.schoolName && seniorSnapshot.schoolId) {
          seniorSnapshot.schoolName = `${seniorSnapshot.schoolName} (${seniorSnapshot.schoolId})`;
        }
        state.seniorSnapshot = seniorSnapshot;
      }
      state.loadingSeniorSnapshot = false;
    });
    builder.addCase(fetchSeniorSnapshotById.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingSeniorSnapshot = false;
      }
    });

    //fetch School Year options
    builder.addCase(fetchSeniorSnapshotSchoolYears.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchSeniorSnapshotSchoolYears.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel.schoolYearOptions = action.payload;
          const defaultValue = action.payload[0] || {
            value: '',
            label: '',
          };

          state.formViewModel.defaultSchoolYear = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
          state.formViewModel.selectedSchoolYear = {
            value: defaultValue.value,
            label: defaultValue.text,
          };
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchSeniorSnapshotSchoolYears.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    //fetch LEA options
    builder.addCase(fetchSeniorSnapshotLeas.pending, (state) => {
      state.formViewModel.schoolYearOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(fetchSeniorSnapshotLeas.fulfilled, (state, action) => {
      if (!isEmptyObject(action.payload)) {
        state.formViewModel.leaOptions = action.payload;
      }
      state.loadingFormViewModel = false;
    });
    builder.addCase(fetchSeniorSnapshotLeas.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.loadingFormViewModel = false;
      }
    });

    //fetch School options
    builder.addCase(fetchSeniorSnapshotSchoolsByLeaId.pending, (state) => {
      state.formViewModel.schoolOptions = [];
      state.loadingFormViewModel = true;
    });
    builder.addCase(
      fetchSeniorSnapshotSchoolsByLeaId.fulfilled,
      (state, action) => {
        if (!isEmptyObject(action.payload)) {
          state.formViewModel.schoolOptions = action.payload;
        }
        state.loadingFormViewModel = false;
      }
    );
    builder.addCase(
      fetchSeniorSnapshotSchoolsByLeaId.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFormViewModel = false;
        }
      }
    );

    builder.addCase(uploadSeniorSnapshotFileThunk.pending, (state) => {
      state.uploadingFile = true;
      state.error = null;
    });
    builder.addCase(uploadSeniorSnapshotFileThunk.fulfilled, (state) => {
      state.uploadingFile = false;
    });
    builder.addCase(uploadSeniorSnapshotFileThunk.rejected, (state, action) => {
      state.uploadingFile = false;
      state.error = action.error.message || 'Failed to upload file';
    });

    builder.addCase(deleteSeniorSnapshotFileThunk.pending, (state) => {
      state.deletingFile = true;
      state.error = null;
    });
    builder.addCase(deleteSeniorSnapshotFileThunk.fulfilled, (state) => {
      state.deletingFile = false;
    });
    builder.addCase(deleteSeniorSnapshotFileThunk.rejected, (state, action) => {
      state.deletingFile = false;
      state.error = action.error.message || 'Failed to delete file';
    });

    builder.addCase(fetchAllSeniorSnapshotFilesThunk.pending, (state) => {
      state.loadingFiles = true;
      state.error = null;
    });
    builder.addCase(
      fetchAllSeniorSnapshotFilesThunk.fulfilled,
      (state, action) => {
        state.files = action.payload || [];
        state.loadingFiles = false;
      }
    );
    builder.addCase(
      fetchAllSeniorSnapshotFilesThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingFiles = false;
          state.error = action.error.message || 'Failed to fetch files';
        }
      }
    );

    builder.addCase(fetchSeniorSnapshotFilesByStudentThunk.pending, (state) => {
      state.loadingStudentFiles = true;
      state.error = null;
    });
    builder.addCase(
      fetchSeniorSnapshotFilesByStudentThunk.fulfilled,
      (state, action) => {
        state.studentFiles = action.payload || [];
        state.loadingStudentFiles = false;
      }
    );
    builder.addCase(
      fetchSeniorSnapshotFilesByStudentThunk.rejected,
      (state, action) => {
        if (!action.meta.aborted) {
          state.loadingStudentFiles = false;
          state.error = action.error.message || 'Failed to fetch student files';
        }
      }
    );

    builder.addCase(fetchDataCollectionCheck.fulfilled, (state, action) => {
      state.dataCollectionCheck = action.payload;
    });
    builder.addCase(fetchDataCollectionCheck.rejected, (state, action) => {
      if (!action.meta.aborted) {
        state.dataCollectionCheck = false;
      }
    });
  },
});

//export reducers
export const {
  clearSeniorSnapshot,
  saveSeniorSnapshot,
  setSeniorSnapshotDataEntryMode,
  setSeniorSnapshotSchoolYear,
  setSeniorSnapshotLea,
  setSeniorSnapshotSchool,
  setSeniorSnapshotReviewStatus,
  setUploadingFile,
  setDownloadingFile,
  setDeletingFile,
  setFiles,
  addFile,
  updateFile,
  removeFile,
  setStudentFiles,
  addStudentFile,
  updateStudentFile,
  removeStudentFile,
  clearSeniorSnapshotError,
} = seniorSnapshotSlice.actions;

// listItems
export const selectSeniorSnapshotLoadingListItems = (
  state: RootState
): boolean => state.seniorSnapshot.loadingListItems;
export const selectSeniorSnapshotListItems = (
  state: RootState
): SeniorSnapshotListItemModel[] => state.seniorSnapshot.listItems;

// seniorSnapshot
export const selectSeniorSnapshotLoadingSeniorSnapshot = (
  state: RootState
): boolean => state.seniorSnapshot.loadingSeniorSnapshot;
export const selectSeniorSnapshotSeniorSnapshot = (
  state: RootState
): SeniorSnapshotModel => state.seniorSnapshot.seniorSnapshot;

// formViewModel
export const selectSeniorSnapshotLoadingFormViewModel = (
  state: RootState
): boolean => state.seniorSnapshot.loadingFormViewModel;

export const selectSeniorSnapshotFormViewModel = (
  state: RootState
): SeniorSnapshotFormViewModel => state.seniorSnapshot.formViewModel;

// dataEntryMode
export const selectSeniorSnapshotDataEntryMode = (
  state: RootState
): DataEntryMode => state.seniorSnapshot.dataEntryMode;

export const selectSeniorSnapshotSchoolYear = (state: RootState): string =>
  state.seniorSnapshot.schoolYear;

export const selectSeniorSnapshotUploadingFile = (state: RootState): boolean =>
  state.seniorSnapshot.uploadingFile;
export const selectSeniorSnapshotDownloadingFile = (
  state: RootState
): boolean => state.seniorSnapshot.downloadingFile;
export const selectSeniorSnapshotDeletingFile = (state: RootState): boolean =>
  state.seniorSnapshot.deletingFile;
export const selectSeniorSnapshotFiles = (
  state: RootState
): SeniorSnapshotFileListItemModel[] => state.seniorSnapshot.files;
export const selectSeniorSnapshotStudentFiles = (
  state: RootState
): SeniorSnapshotFileListItemModel[] => state.seniorSnapshot.studentFiles;
export const selectSeniorSnapshotLoadingFiles = (state: RootState): boolean =>
  state.seniorSnapshot.loadingFiles;
export const selectSeniorSnapshotLoadingStudentFiles = (
  state: RootState
): boolean => state.seniorSnapshot.loadingStudentFiles;
export const selectSeniorSnapshotError = (state: RootState): string | null =>
  state.seniorSnapshot.error;

export const selectSeniorSnapshotDataCollectionCheck = (
  state: RootState
): boolean => state.seniorSnapshot.dataCollectionCheck;

export default seniorSnapshotSlice.reducer;
